import { Inject, Injectable, InjectionToken } from '@angular/core';

/**
 * Factory function that provides the global `navigator` object.
 *
 * @remarks
 * Creates a factory function for Angular dependency injection to access the browser's `Navigator API`.
 * Used with {@link NAVIGATOR_TOKEN} to provide browser information and capabilities in a testable way.
 *
 * @returns The global `navigator` object
 *
 * @example
 *
 * ```typescript
 * @Component({
 *   providers: [{ provide: NAVIGATOR_TOKEN, useFactory: navigatorFactory }]
 * })
 * export class MyComponent {}
 * ```
 */
export function navigatorFactory(): Navigator {
  return navigator;
}

/**
 * Injection token for the browser's Navigator object.
 *
 * @example
 *
 * ```typescript
 * @Component({
 *   providers: [{ provide: NAVIGATOR_TOKEN, useFactory: navigatorFactory }]
 * })
 * export class EllipsisComponent {}
 * ```
 */
export const NAVIGATOR_TOKEN = new InjectionToken<Navigator>('Navigator object');

/**
 * Service for detecting browser capabilities and user agent information.
 *
 * @remarks
 * Provides utility methods to detect specific browser types and features. Uses dependency injection
 * with {@link NAVIGATOR_TOKEN} to access the Navigator API in a testable way. Primarily used for
 * browser-specific workarounds and feature detection.
 *
 * @example
 *
 * ```typescript
 * @Component({
 *   providers: [BrowserService, { provide: NAVIGATOR_TOKEN, useFactory: navigatorFactory }]
 * })
 * export class MyComponent {
 *   constructor(private readonly browserService: BrowserService) {}
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class BrowserService {
  // 'any' due to issue https://github.com/angular/angular/issues/15640
  constructor(@Inject(NAVIGATOR_TOKEN) private readonly navigator: any) {}

  /**
   * Detects if the current browser is Microsoft Internet Explorer.
   *
   * @remarks
   * Checks the user agent string for IE-specific identifiers including legacy 'MSIE'
   * and modern 'Trident/' tokens. Used for applying IE-specific workarounds and features
   * in components that need browser-specific behavior.
   *
   * @returns `true` if the browser is Internet Explorer, `false` otherwise
   *
   * @example
   *
   * ```typescript
   * export class EllipsisComponent {
   *   constructor(private readonly browserService: BrowserService) {}
   *
   *   get isIE(): boolean {
   *     return this.browserService.browserIsMSIE();
   *   }
   * }
   * ```
   */
  browserIsMSIE(): boolean {
    return this.navigator.userAgent.indexOf('MSIE') > -1 || this.navigator.userAgent.indexOf('Trident/') > -1;
  }
}
